import { getShellBridge } from './shellBridge';
import { getShellInfo, isInShell, useShellInfo, type ShellInfo } from './shellPresence';

export type ShareLinkData = { url: string; text?: string; title?: string };

// TODO: drop the casts once a @sunflower-games/webview-bridge with share.open is installed
const hasShareCapability = (info: ShellInfo | null): boolean =>
  (info as { capabilities?: string[] } | null)?.capabilities?.includes('share') === true;

const hasWebShare = (): boolean => typeof navigator.share === 'function';

/** Whether the shell opens its native share sheet — Android WebView has no `navigator.share`. */
export function canShareInShell(): boolean {
  return isInShell() && hasShareCapability(getShellInfo());
}

/** Whether a share button can do anything here: the Web Share API, or the shell's share sheet. */
export function canShareLink(): boolean {
  return hasWebShare() || canShareInShell();
}

/** {@link canShareLink}, re-rendering when the shell confirms. */
export function useCanShareLink(): boolean {
  const info = useShellInfo();
  return hasWebShare() || hasShareCapability(info);
}

/**
 * Shares through the Web Share API when the browser has it, otherwise through the shell (`share.open`).
 * @returns Whether a share sheet was opened; rejects as `navigator.share` does (e.g. `AbortError`).
 */
export async function shareLink({ url, text, title }: ShareLinkData): Promise<boolean> {
  if (hasWebShare()) {
    await navigator.share({ url, text, title });
    return true;
  }

  const bridge = canShareInShell() ? getShellBridge() : null;
  if (bridge === null) return false;

  const absoluteUrl = new URL(url, window.location.origin).href;
  bridge.send('share.open' as never, { url: absoluteUrl, ...(text && { text }), ...(title && { title }) } as never);
  return true;
}
